import { compact, getSlotCompoundVariant, memo, splitProps } from '../helpers.mjs';
import { createRecipe } from './create-recipe.mjs';

const floatingButtonDefaultVariants = {
  "extended": false,
  "textOnly": false,
  "size": "md",
  "color": "primary"
}
const floatingButtonCompoundVariants = [
  {
    "extended": false,
    "size": "md",
    "css": {
      "root": {
        "width": "{sizes.floatingButton.size.md.container}",
        "height": "{sizes.floatingButton.size.md.container}",
        "minWidth": "{sizes.floatingButton.size.md.container}",
        "minHeight": "{sizes.floatingButton.size.md.container}"
      }
    }
  },
  {
    "extended": false,
    "size": "sm",
    "css": {
      "root": {
        "width": "{sizes.floatingButton.size.sm.container}",
        "height": "{sizes.floatingButton.size.sm.container}",
        "minWidth": "{sizes.floatingButton.size.sm.container}",
        "minHeight": "{sizes.floatingButton.size.sm.container}"
      }
    }
  },
  {
    "extended": true,
    "size": "md",
    "css": {
      "root": {
        "paddingTop": "{spacing.floatingButton.size.md.paddingY}",
        "paddingBottom": "{spacing.floatingButton.size.md.paddingY}",
        "paddingLeft": "{spacing.floatingButton.size.md.paddingLeft}",
        "paddingRight": "{spacing.floatingButton.size.md.paddingRight}",
        "gap": "{spacing.floatingButton.size.md.gap}",
        "&[data-has-icon]": {
          "paddingLeft": "{spacing.floatingButton.size.md.paddingLeftWithIcon}",
          "paddingRight": "{spacing.floatingButton.size.md.paddingRightWithIcon}"
        },
        "&[data-has-end-icon]": {
          "paddingRight": "{spacing.floatingButton.size.md.paddingRightWithEndIcon}"
        },
        "fontFamily": "default",
        "fontSize": "15",
        "lineHeight": "26",
        "fontWeight": "button.default",
        "_themeEducoreAll": {
          "fontFamily": "default",
          "fontSize": "14",
          "lineHeight": "24",
          "fontWeight": "button.default"
        }
      },
      "icon": {
        "fontSize": "{sizes.floatingButton.size.md.icon.extended}",
        "& > svg": {
          "width": "{sizes.floatingButton.size.md.icon.extended}",
          "height": "{sizes.floatingButton.size.md.icon.extended}"
        }
      }
    }
  },
  {
    "extended": true,
    "size": "sm",
    "css": {
      "root": {
        "paddingTop": "{spacing.floatingButton.size.sm.paddingY}",
        "paddingBottom": "{spacing.floatingButton.size.sm.paddingY}",
        "paddingLeft": "{spacing.floatingButton.size.sm.paddingLeft}",
        "paddingRight": "{spacing.floatingButton.size.sm.paddingRight}",
        "gap": "{spacing.floatingButton.size.sm.gap}",
        "&[data-has-icon]": {
          "paddingLeft": "{spacing.floatingButton.size.sm.paddingLeftWithIcon}",
          "paddingRight": "{spacing.floatingButton.size.sm.paddingRightWithIcon}"
        },
        "&[data-has-end-icon]": {
          "paddingRight": "{spacing.floatingButton.size.sm.paddingRightWithEndIcon}"
        },
        "fontFamily": "default",
        "fontSize": "14",
        "lineHeight": "24",
        "fontWeight": "button.default"
      },
      "icon": {
        "fontSize": "{sizes.floatingButton.size.sm.icon.extended}",
        "& > svg": {
          "width": "{sizes.floatingButton.size.sm.icon.extended}",
          "height": "{sizes.floatingButton.size.sm.icon.extended}"
        }
      }
    }
  },
  {
    "extended": true,
    "textOnly": false,
    "size": "md",
    "css": {
      "spinner": {
        "& [role=\"progressbar\"]": {
          "width": "{sizes.floatingButton.size.md.spinner.extended}!",
          "height": "{sizes.floatingButton.size.md.spinner.extended}!"
        }
      }
    }
  },
  {
    "extended": true,
    "textOnly": false,
    "size": "sm",
    "css": {
      "spinner": {
        "& [role=\"progressbar\"]": {
          "width": "{sizes.floatingButton.size.sm.spinner.extended}!",
          "height": "{sizes.floatingButton.size.sm.spinner.extended}!"
        }
      }
    }
  },
  {
    "color": "primary",
    "extended": false,
    "css": {
      "root": {
        "_themeEducoreAll": {
          "_hover": {
            "background": "linear-gradient(0deg, token(colors.overlay.darken.hover) 0%, token(colors.overlay.darken.hover) 100%), token(colors.button.filled.bg.default)"
          },
          "_active": {
            "background": "linear-gradient(0deg, token(colors.overlay.darken.pressed) 0%, token(colors.overlay.darken.pressed) 100%), token(colors.button.filled.bg.default)"
          }
        }
      }
    }
  },
  {
    "color": "impact",
    "extended": false,
    "css": {
      "root": {
        "_themeEducoreAll": {
          "_hover": {
            "backgroundImage": "linear-gradient(0deg, token(colors.overlay.darken.hover) 0%, token(colors.overlay.darken.hover) 100%), token(colors.gradient.primary.default)",
            "boxShadow": "none",
            "transition": "none",
            "animation": "none"
          },
          "_active": {
            "backgroundImage": "linear-gradient(0deg, token(colors.overlay.darken.pressed) 0%, token(colors.overlay.darken.pressed) 100%), token(colors.gradient.primary.default)",
            "boxShadow": "none",
            "transition": "none",
            "animation": "none"
          }
        }
      }
    }
  },
  {
    "color": "secondary",
    "extended": false,
    "css": {
      "root": {
        "_themeEducoreAll": {
          "_hover": {
            "background": "neutral.surface.low",
            "boxShadow": "inset 0 0 0 1px token(colors.orange.light.500), 0 4px 16px 0 token(colors.neutral.ghostBg.high)"
          }
        }
      }
    }
  }
]

const floatingButtonSlotNames = [
  [
    "root",
    "floatingButton__root"
  ],
  [
    "icon",
    "floatingButton__icon"
  ],
  [
    "spinner",
    "floatingButton__spinner"
  ]
]
const floatingButtonSlotFns = /* @__PURE__ */ floatingButtonSlotNames.map(([slotName, slotKey]) => [slotName, createRecipe(slotKey, floatingButtonDefaultVariants, getSlotCompoundVariant(floatingButtonCompoundVariants, slotName))])

const floatingButtonFn = memo((props = {}) => {
  return Object.fromEntries(floatingButtonSlotFns.map(([slotName, slotFn]) => [slotName, slotFn.recipeFn(props)]))
})

const floatingButtonVariantKeys = [
  "size",
  "extended",
  "textOnly",
  "color"
]
const getVariantProps = (variants) => ({ ...floatingButtonDefaultVariants, ...compact(variants) })

export const floatingButton = /* @__PURE__ */ Object.assign(floatingButtonFn, {
  __recipe__: false,
  __name__: 'floatingButton',
  raw: (props) => props,
  classNameMap: {},
  variantKeys: floatingButtonVariantKeys,
  variantMap: {
  "size": [
    "md",
    "sm"
  ],
  "extended": [
    "true",
    "false"
  ],
  "textOnly": [
    "true",
    "false"
  ],
  "color": [
    "impact",
    "primary",
    "secondary",
    "tertiary"
  ]
},
  splitVariantProps(props) {
    return splitProps(props, floatingButtonVariantKeys)
  },
  getVariantProps
})